import {Box, Card, Grid, SectionIntro} from '@primer/react-brand/esm'
import {getPrimerIcon} from '@github-ui/swp-core/lib/utils/icons'
import type {Document} from '@contentful/rich-text-types'
import {renderWithoutParagraphs} from '../../utils'
import baseStyles from '../../styles/base.module.css'
import type {IconLinkItem} from '../../types'
import type {ComponentProps} from 'react'
import {clsx} from 'clsx'

const defaultColumnSpan = {medium: 4} as const

export type FlexSuiteOutroResourcesProps = {
  outroResourcesHeading?: Document
  outroResources?: IconLinkItem[]
  columnSpan?: ComponentProps<typeof Grid.Column>['span']
}

export function FlexSuiteOutroResources({
  outroResourcesHeading,
  outroResources,
  columnSpan = defaultColumnSpan,
}: FlexSuiteOutroResourcesProps) {
  if (!outroResources?.length) return null

  return (
    <>
      {outroResourcesHeading ? (
        <Box paddingBlockStart={{narrow: 'spacious', regular: 64}} paddingBlockEnd={{narrow: 'spacious', regular: 64}}>
          <SectionIntro align="center" fullWidth>
            <SectionIntro.Heading size="3">{renderWithoutParagraphs(outroResourcesHeading)}</SectionIntro.Heading>
          </SectionIntro>
        </Box>
      ) : null}
      <Box className={clsx(baseStyles.gridFrameTop, baseStyles.gridFrameBottom)}>
        <Grid columnGap="none" className={baseStyles.gridContent}>
          {outroResources.map(resource => {
            const Octicon = getPrimerIcon(resource.fields.icon)
            return (
              <Grid.Column
                key={resource.sys.id}
                span={columnSpan}
                // eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals
                className="gridColumn"
                data-testid={`outro-resource-column-${resource.sys.id}`}
                data-column-span={typeof columnSpan === 'number' ? columnSpan : columnSpan.xlarge}
              >
                <Box
                  className={baseStyles.gridItem}
                  padding={{narrow: 24, regular: 40, wide: 64}}
                  paddingBlockStart={{regular: 80}}
                  paddingBlockEnd={{regular: 80}}
                  borderWidth="thin"
                  borderColor="muted"
                  style={{height: '100%'}}
                >
                  <Card
                    href={resource.fields.href}
                    variant="minimal"
                    fullWidth
                    ctaVariant="arrow"
                    ctaText={resource.fields.label}
                  >
                    {Octicon ? <Card.Icon icon={<Octicon />} color="default" hasBackground /> : null}
                    {resource.fields.eyebrow ? <Card.Label>{resource.fields.eyebrow}</Card.Label> : null}
                    <Card.Heading>
                      {renderWithoutParagraphs(resource.fields.heading)}
                      {resource.fields.alt ? <span className="sr-only"> {resource.fields.alt}</span> : null}
                    </Card.Heading>
                    <Card.Description>{renderWithoutParagraphs(resource.fields.description)}</Card.Description>
                  </Card>
                </Box>
              </Grid.Column>
            )
          })}
        </Grid>
      </Box>
    </>
  )
}
